<script setup lang="ts">
// import { RouterLink } from "vue-router";
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { getSessionStorageReac } from "@/assets/js/helpers";
import { useDefaultStore } from "@/store";
import { useToast } from "primevue/usetoast";
import tmdbLogo from "@/assets/images/tmdb/blue_long_2-9665a76b1ae401a510ec1e0ca40ddcb3b0cfe45f1d51b77a308fea0845885648.svg";

definePage({
	name: "MoviesBeatTheClockPage",
});

type TMovie = {
	id: string | number;
	title: string;
	original_title: string;
	poster_path: string | null;
	backdrop_path: string | null;
};

type TFeedbackState = "idle" | "loading" | "correct" | "wrong";

const GAME_TOKEN_KEY = "game-token";

const store = useDefaultStore();
const router = useRouter();
const toast = useToast();

const movies = ref<TMovie[]>([]);
const isSubmitting = ref(false);
const isLocked = ref(false);
const isGameOver = ref(false);
const isExpiredGameOver = ref(false);
const hasReportedExpiration = ref(false);

// Keep round state internally for now, even if UI shows score instead.
const currentRound = ref(1);
const nextRound = ref(1);

// Visible score for BTC mode.
const score = ref(0);

const feedbackState = ref<TFeedbackState>("idle");
const moviesVisible = ref(true);

const timeLeft = ref(0);
let timerInterval: ReturnType<typeof setInterval> | null = null;

const gameToken = getSessionStorageReac<string | null>(
	GAME_TOKEN_KEY,
	false,
	null,
);

function getBackdropUrl(path: string | null) {
	if (!path) return "";
	return `https://image.tmdb.org/t/p/original${path}`;
}

function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function clearTimer() {
	if (timerInterval) {
		clearInterval(timerInterval);
		timeLeft.value = 0;
		timerInterval = null;
	}
}

function isTimeExpiredFromApi(data: any): boolean {
	return (
		data?.isExpired === true ||
		data?.timeExpired === true ||
		data?.secondsLeft === 0
	);
}

// Normalize different possible finish flags coming from backend.
function isFinishedFromApi(data: any): boolean {
	return data?.isFinished === true || isTimeExpiredFromApi(data);
}

// Mark the game as finished and report expiration only once.
async function expireGame(showPlayAgain = true) {
	if (isGameOver.value) {
		return;
	}

	isGameOver.value = true;
	isExpiredGameOver.value = showPlayAgain;
	isLocked.value = true;
	feedbackState.value = "wrong";
	clearTimer();

	const currentGameToken = gameToken.value;

	if (!currentGameToken || hasReportedExpiration.value) {
		return;
	}

	hasReportedExpiration.value = true;

	try {
		await api.postBTCGameExpired({
			token: currentGameToken,
		});
	} catch (err: any) {
		console.warn(err.message);
	}
}

// Start a local countdown from the value returned by API.
function startTimer(seconds: number) {
	clearTimer();

	timeLeft.value = Math.max(0, Math.ceil(seconds));

	if (timeLeft.value <= 0) {
		void expireGame();
		return;
	}

	timerInterval = setInterval(() => {
		if (timeLeft.value > 0) {
			timeLeft.value -= 1;
		}

		if (timeLeft.value <= 0) {
			clearTimer();
			void expireGame();
		}
	}, 1000);
}

// Reset local page state before starting a fresh BTC session.
function resetGameState() {
	movies.value = [];
	isSubmitting.value = false;
	isLocked.value = false;
	isGameOver.value = false;
	isExpiredGameOver.value = false;
	hasReportedExpiration.value = false;
	currentRound.value = 1;
	nextRound.value = 1;
	score.value = 0;
	feedbackState.value = "idle";
	moviesVisible.value = true;
	timeLeft.value = 0;
	clearTimer();
}

async function playAgain() {
	gameToken.value = null;
	resetGameState();
	await getInitialPair();
}

// On page load, either restore the existing BTC game or create a new one.
async function initializeGame() {
	const existingGameToken = gameToken.value;

	if (existingGameToken) {
		console.log("existing game token", existingGameToken);
		await getMoviesSinglePayerState(existingGameToken);
		return;
	}

	await getInitialPair();
}

async function getInitialPair() {
	try {
		const res = await api.getMoviesInitialPair({
			token: store.userData?.usr_token ?? "",
			type: store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
			genreIds: store.userData?.usr_mov_gnr_ids ?? null,
			fromYear: store.userData?.usr_movies_min_year ?? null,
			toYear: store.userData?.usr_movies_max_year ?? null,
			countryCodes: store.userData?.usr_mov_ori_cty_code
				? [store.userData?.usr_mov_ori_cty_code]
				: null,
			popularity: store.userData?.usr_movies_popularity ?? "popular",
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.movies)) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: data?.showText
					? data?.message
					: "An unexpected error occurred. Please try again.",
				life: 3500,
			});

			await router.push({
				name: "MoviesPage",
			});

			return;
		}

		const returnedGameToken = data.token ?? null;

		if (returnedGameToken) {
			gameToken.value = returnedGameToken;
		}

		movies.value = data.movies;
		currentRound.value = 1;
		nextRound.value = data.nextRound ?? 1;

		// Adjust this key if backend returns score under a different name.
		score.value = data.score ?? 0;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

	} catch (err: any) {
		console.warn(err.message);
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText
				? customErrorMsg
				: "An unexpected error occurred. Please try again.",
			life: 3500,
		});

		await router.push({
			name: "MoviesPage",
		});
	}
}

async function getMoviesSinglePayerState(currentGameToken: string) {
	try {
		const res = await api.getMoviesSinglePayerState({
			token: currentGameToken,
			type: store.SINGLE_PLAYER_GAME_TYPES.BEAT_THE_CLOCK,
		});
		const data = res.data.data;

		if (!data?.ok || !Array.isArray(data.movies)) {
			gameToken.value = null;
			await getInitialPair();
			return;
		}

		movies.value = data.movies;
		currentRound.value = data.round ?? 1;
		nextRound.value = (data.round ?? 1) + 1;

		// Restore score from backend state.
		score.value = data.score ?? 0;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

	if (isFinishedFromApi(data)) {
		await expireGame(isTimeExpiredFromApi(data));
	}
	} catch (err: any) {
		console.warn(err.message);
		gameToken.value = null;
		await getInitialPair();
	}
}

// Small transition used after both correct and wrong answers.
async function animateAnswerTransition(
	nextMovies: TMovie[],
	resolvedNextRound: number,
	state: "correct" | "wrong",
) {
	feedbackState.value = state;

	await sleep(500);

	moviesVisible.value = false;

	await sleep(260);

	movies.value = nextMovies;
	currentRound.value = resolvedNextRound;
	nextRound.value = resolvedNextRound;

	await nextTick();

	moviesVisible.value = true;

	await sleep(260);

	if (!isGameOver.value) {
		feedbackState.value = "idle";
	}
}

async function handleAnswer(clickedIndex: 0 | 1) {
	if (movies.value.length !== 2 || isSubmitting.value || isLocked.value) {
		return;
	}

	const vote = movies.value[clickedIndex].id;
	const existingGameToken = gameToken.value;

	try {
		isSubmitting.value = true;
		feedbackState.value = "loading";

		const res = await api.postBTCHandleAnswer({
			vote,
			token: existingGameToken ?? "",
			genreIds: store.userData?.usr_mov_gnr_ids ?? null,
			minMovieYear: store.userData?.usr_movies_min_year ?? null,
			maxMovieYear: store.userData?.usr_movies_max_year ?? null,
			countryCodes: store.userData?.usr_mov_ori_cty_code
				? [store.userData?.usr_mov_ori_cty_code]
				: null,
			popularity: store.userData?.usr_movies_popularity ?? "popular",
		});

		const data = res.data.data;
		// console.log("handleAnswer", data);

		if (!data?.ok) {
			feedbackState.value = "idle";
			return;
		}

		// Update score on every answer if backend returns it.
		score.value = data.score ?? score.value;

		if (typeof data.secondsLeft === "number") {
			startTimer(data.secondsLeft);
		}

		if (isFinishedFromApi(data)) {
			await expireGame(isTimeExpiredFromApi(data));
			return;
		}

		if (data.movies && data.movies.length === 2) {
			const resolvedNextRound = data.nextRound ?? currentRound.value + 1;

			await animateAnswerTransition(
				data.movies,
				resolvedNextRound,
				data.isCorrect ? "correct" : "wrong",
			);
			return;
		}

		feedbackState.value = "idle";
	} catch (err: any) {
		console.warn(err.message);
		feedbackState.value = "idle";
		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText
				? customErrorMsg
				: "An unexpected error occurred. Please try again.",
			life: 3500,
		});

		await router.push({
			name: "MoviesPage",
		});
	} finally {
		isSubmitting.value = false;
	}
}

onMounted(() => {
	initializeGame();
});

onBeforeUnmount(() => {
	clearTimer();
});

useHead({
	title: getPageTitle("Movies - Beat The Clock"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Movies - Beat The Clock"),
		},
		{
			name: "description",
			content:
				"Beat the timer, make quick choices, and guess which movie came first.",
		},
		{ name: "robots", content: "index, follow" },
		{
			property: "og:title",
			content: getPageTitle("Movies - Beat The Clock"),
		},
		{
			property: "og:description",
			content:
				"Beat the timer, make quick choices, and guess which movie came first.",
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<section
		v-if="movies.length === 2"
		class="movies-classic-game-page"
		:class="{
			'movies-classic-game-page--correct': feedbackState === 'correct',
			'movies-classic-game-page--wrong': feedbackState === 'wrong',
			'movies-classic-game-page--loading-state': feedbackState === 'loading',
		}"
	>
		<RouterLink
			to="/movies"
			class="category-back-link"
		>
			← Back
		</RouterLink>

		<!-- <div class="round-badge">Round {{ currentRound }}</div> -->
		<div class="round-badge">{{ score }} pts</div>
		<div
			class="timer-badge"
			:class="{ 'is-low': timeLeft <= 10 }"
		>
			{{ timeLeft }}s
		</div>

		<div
			v-if="currentRound === 1"
			class="instruction-badge"
		>
			Pick the movie with the earlier release date
		</div>

		<div
			v-for="(movie, index) in movies"
			:key="index"
			class="movie-panel"
			:class="{
				'is-disabled': isSubmitting || isLocked,
				'is-fading': !moviesVisible,
			}"
			:style="{
				backgroundImage: movie.backdrop_path
					? `url(${getBackdropUrl(movie.backdrop_path)})`
					: 'none',
			}"
			@click="handleAnswer(index as 0 | 1)"
		>
			<div class="movie-panel__overlay"></div>

			<div class="movie-panel__content">
				<h2 class="movie-title">
					{{
						store.userData?.usr_movies_show_original_title
							? movie.original_title
							: movie.title
					}}
				</h2>
			</div>
		</div>

		<div
			class="or-badge"
			:class="{
				'is-loading': feedbackState === 'loading',
				'is-correct': feedbackState === 'correct',
				'is-wrong': feedbackState === 'wrong',
			}"
		>
			<span v-if="feedbackState === 'correct'">✓</span>
			<span v-else-if="feedbackState === 'wrong'">✕</span>
			<span v-else-if="feedbackState === 'loading'">...</span>
			<span v-else>OR</span>
		</div>
		<div
			v-if="isExpiredGameOver"
			class="wrong-actions"
		>
			<button
				type="button"
				class="play-again-button"
				aria-label="Play again"
				title="Play again"
				@click="playAgain"
			>
				<i-fa-rotate-right
					class="play-again-button__icon"
					aria-hidden="true"
				/>
			</button>
		</div>

		<div class="tmdb-attribution">
			<a
				href="https://www.themoviedb.org/"
				target="_blank"
				rel="noopener noreferrer"
				class="tmdb-link"
			>
				<img
					:src="tmdbLogo"
					alt="TMDB"
					class="tmdb-logo"
				/>
			</a>

			<div class="tmdb-text">
				This product uses the TMDB API but is not endorsed or certified by TMDB.
			</div>
		</div>
	</section>

	<section
		v-else
		class="movies-classic-game-page movies-classic-game-page--loading"
	>
		<div class="loading-badge">
			<div class="loading-spinner"></div>
		</div>

		<div class="tmdb-attribution">
			<a
				href="https://www.themoviedb.org/"
				target="_blank"
				rel="noopener noreferrer"
				class="tmdb-link"
			>
				<img
					:src="tmdbLogo"
					alt="TMDB"
					class="tmdb-logo"
				/>
			</a>

			<div class="tmdb-text">
				This product uses the TMDB API but is not endorsed or certified by TMDB.
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.movie-panel__overlay {
	position: absolute;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	transition:
		background 0.25s ease,
		opacity 0.25s ease;
}

.movies-classic-game-page {
	position: relative;
	width: 100%;
	min-height: 100dvh;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr 1fr;
	background: #111;
	overflow: hidden;

	&--correct {
		.movie-panel__overlay {
			background: rgba(20, 60, 28, 0.34);
		}
	}

	&--wrong {
		.movie-panel__overlay {
			background: rgba(95, 20, 20, 0.44);
		}
	}
}

.back-link,
.round-badge {
	position: absolute;
	z-index: 5;
	padding: 8px 12px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.22);
	color: rgba(255, 255, 255, 0.78);
	font-size: 14px;
	font-weight: 600;
	backdrop-filter: blur(6px);
	transition:
		background 0.2s ease,
		color 0.2s ease,
		opacity 0.2s ease;
}

.back-link {
	top: 16px;
	left: 16px;
	text-decoration: none;

	&:hover {
		background: rgba(0, 0, 0, 0.34);
		color: rgb(255, 255, 255);
	}
}

.round-badge {
	top: 16px;
	right: 16px;
	pointer-events: none;
}

.instruction-badge {
	position: absolute;
	top: calc(50% - 112px);
	left: 50%;
	transform: translateX(-50%);
	z-index: 5;
	padding: 6px 10px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	backdrop-filter: blur(6px);
	max-width: calc(100% - 32px);
	pointer-events: none;
}

.movie-panel {
	position: relative;
	min-height: 50dvh;
	background-color: rgb(26, 26, 26);
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	cursor: pointer;
	overflow: hidden;
	opacity: 1;
	transform: scale(1);
	transition:
		opacity 0.32s ease,
		transform 0.32s ease,
		filter 0.32s ease;

	&.is-disabled {
		pointer-events: none;
	}

	&.is-fading {
		opacity: 0;
		transform: scale(1.025);
	}

	&:hover {
		.movie-panel__overlay {
			background: rgba(0, 0, 0, 0.28);
		}
	}
}

.movie-panel__content {
	position: relative;
	z-index: 1;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	text-align: center;
}

.movie-title {
	margin: 0;
	color: rgb(255, 255, 255);
	font-size: clamp(28px, 5vw, 64px);
	font-weight: 800;
	line-height: 1.1;
	text-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
	user-select: none;
}

.or-badge,
.loading-badge {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%) scale(1);
	z-index: 3;
	width: 84px;
	height: 84px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: white;
	color: rgb(17, 17, 17);
	font-size: 24px;
	font-weight: 900;
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
	pointer-events: none;
	transition:
		background 0.25s ease,
		color 0.25s ease,
		transform 0.25s ease,
		box-shadow 0.25s ease,
		opacity 0.25s ease;

	&.is-loading {
		transform: translate(-50%, -50%) scale(0.96);
	}

	&.is-correct {
		background: rgb(49, 180, 90);
		color: rgb(255, 255, 255);
		transform: translate(-50%, -50%) scale(1.08);
		box-shadow: 0 12px 36px rgba(49, 180, 90, 0.35);
	}

	&.is-wrong {
		background: rgb(208, 62, 62);
		color: rgb(255, 255, 255);
		transform: translate(-50%, -50%) scale(1.08);
		box-shadow: 0 12px 36px rgba(208, 62, 62, 0.35);
	}
}

.loading-spinner {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	border: 4px solid rgba(17, 17, 17, 0.14);
	border-top-color: rgb(17, 17, 17);
	animation: loading-spin 0.85s linear infinite;
}

.wrong-actions {
	position: absolute;
	left: 50%;
	top: calc(50% + 76px);
	transform: translateX(-50%);
	z-index: 4;
	display: flex;
	justify-content: center;
}

.play-again-button {
	border: 0;
	outline: 0;
	width: 42px;
	height: 42px;
	padding: 0;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.14);
	color: rgb(255, 255, 255);
	cursor: pointer;
	backdrop-filter: blur(8px);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		transform 0.2s ease,
		background 0.2s ease,
		opacity 0.2s ease;

	&:hover {
		transform: translateY(-1px);
		background: rgba(255, 255, 255, 0.2);
	}
}

.play-again-button__icon {
	width: 20px;
	height: 20px;
	display: block;
}

.tmdb-attribution {
	position: absolute;
	left: 50%;
	bottom: 10px;
	transform: translateX(-50%);
	z-index: 6;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 6px 18px;
	border-radius: 999px;
	background: rgba(0, 0, 0, 0.18);
	color: rgba(255, 255, 255, 0.62);
	font-size: 10px;
	font-weight: 500;
	line-height: 1.2;
	text-align: center;
	backdrop-filter: blur(6px);
	width: calc(100% - 16px);
	max-width: 720px;
	pointer-events: auto;
}

.tmdb-link {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 auto;
	pointer-events: auto;
}

.tmdb-logo {
	display: block;
	height: 11px;
	width: auto;
	opacity: 0.9;
	transition: opacity 0.2s ease;
}

.tmdb-link:hover .tmdb-logo {
	opacity: 1;
}

.tmdb-text {
	min-width: 0;
	text-align: center;
}

.timer-badge {
	position: absolute;
	top: 56px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 5;
	padding: 10px 16px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.14);
	color: rgb(255, 255, 255);
	font-size: 18px;
	font-weight: 900;
	letter-spacing: 0.03em;
	backdrop-filter: blur(8px);
	box-shadow: 0 10px 30px rgba(0, 0, 0, 0.22);

	&.is-low {
		background: rgba(208, 62, 62, 0.22);
	}
}

@keyframes loading-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (min-width: 768px) {
	.movies-classic-game-page {
		grid-template-columns: 1fr 1fr;
		grid-template-rows: 1fr;
	}

	.movie-panel {
		min-height: 100dvh;
	}

	.back-link {
		top: 20px;
		left: 20px;
	}

	.round-badge {
		top: 20px;
		right: 20px;
	}

	.instruction-badge {
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		max-width: min(440px, calc(100% - 180px));
		font-size: 11px;
		padding: 6px 12px;
	}

	.wrong-actions {
		top: 50%;
		left: calc(50% + 110px);
		transform: translateY(-50%);
		justify-content: flex-start;
	}

	.tmdb-attribution {
		bottom: 14px;
		gap: 8px;
		padding: 6px 12px;
		font-size: 11px;
		width: auto;
		max-width: min(720px, calc(100% - 32px));
	}

	.tmdb-logo {
		height: 12px;
	}

	@media (min-width: 768px) {
		.timer-badge {
			top: 72px;
		}
	}
}
</style>
